/**
 * port_terms.ts — the words of a port run (space campaign E, 2026-09-30). PURE: no DOM, no connection.
 *
 * Every screen that shows a port run reads these three functions, so the terms the window names before you accept,
 * the reasons the dock gives when nothing is offered and the receipt chain after a delivery are one text each:
 *
 *   termsLines   THE TERMS (report §17.2 / §5B step 1): owner now and after default, the custodian (your hull), where
 *                it is landed and who may land it, the reward held, collateral (none), the payload, the deadline, the
 *                loss rule, what happens when the endpoint cannot pay or build, the yard's short-pay risk, the split.
 *   dryLines     THE DRY CHAIN (§7.2 "Bootstrap issue", §12.4): why the port offers no up run, in the server's own
 *                numbers (the hourly step's `port_status` row) — never a client re-derivation.
 *   receiptLines THE RECEIPT CHAIN (§4.1, §22.2 "Explanatory"): accepted → custody → delivered → the yard's order →
 *                a hull → the store → the war, saying only what the data proves (the ore is pooled: "a hull drew on a
 *                stock this run fed", never "this lot became that hull"), or the specific reason it stands still.
 */
import {
  PORT_LOT_U, PORT_HAUL_TTL_S, PORT_RELEASE_U, HULL_ORE_U, HULLS_MAX, PORT_SPLIT_BPS, RECEIPT_FOLLOW_S,
  splitReward, termsShares, yardPayQuote, type ShareIn, type DryCode,
} from '@shared/space_economy.js';

export interface TermsLine { label: string; text: string; warn?: boolean }

export interface TermsInput {
  direction: 'up' | 'down';
  haulId: bigint;
  qty: number;
  familyLabel: string;          // "ore"
  rewardSov: bigint;            // what the port escrowed
  portName: string;             // "the Aurelian Planetary Port"
  cityName: string;             // the port city
  yardName: string;             // "Aurel Prime Yards"
  stationKey: string;           // the yard's station
  hullName: string;             // your active hull
  holdFree: number;             // its free hold (u)
  yardCash: bigint | null;      // the yard firm's wallet now (null = not known here)
  yardMark: bigint | null;      // the yard's live ore mark, ⓒ/u
  captainKey: string; captainName: string;
  crew: Array<{ key: string; name: string; bps: number }>;
  partnerName: string;          // '' = none
}

/** ⓒ with thousands separators. */
export function sov(n: bigint): string { return `${n.toLocaleString('en-US')} ⓒ`; }
/** Basis points as a percent ("60 %", "33.33 %"). */
export function pct(bps: number): string { const p = bps / 100; return `${Number.isInteger(p) ? p : p.toFixed(2)} %`; }

/** The split a run's terms would pay, in order (the captain first), with each share's part of `reward`. */
export function splitPreview(reward: bigint, captainKey: string, crew: readonly ShareIn[]): Array<{ key: string; bps: number; sov: bigint }> {
  const shares = termsShares(captainKey, crew);
  const parts = splitReward(reward, shares);
  return shares.map((s, i) => ({ key: s.key, bps: s.bps, sov: parts[i]?.sov ?? 0n }));
}

/** THE TERMS — one line per term, each named separately (owner ≠ custodian ≠ payment beneficiary). */
export function termsLines(t: TermsInput): TermsLine[] {
  const hours = Math.round(PORT_HAUL_TTL_S / 3600);
  const up = t.direction === 'up';
  const out: TermsLine[] = [
    { label: 'Owner now', text: `${t.portName} — the ${t.qty} u stay the port's while you fly them (crates tagged h:${t.haulId}); no one can sell, unload or land them into a bag` },
    { label: 'Owner after default', text: `the port again — a lapsed run's crates go back to its warehouse; a run lost with its hull leaves them in the wreck, where the port's title ends at the first strip` },
    { label: 'Custodian', text: `your active hull ${t.hullName} (${t.holdFree} u free; the run is ${t.qty} u)`, warn: t.holdFree < t.qty },
    { label: 'Delivery', text: up ? `dock at ${t.yardName} (${t.stationKey}) and land the run there` : `land it at ${t.cityName}'s spaceport on the Commons (H)` },
    { label: 'Hand-in rights', text: `only the run's custodian can land it — you, or the ONE pilot you let take it over (one relay per run; your hull can no longer deliver after it)` },
    { label: 'Reward held', text: `${sov(t.rewardSov)} escrowed by the port when it offered the run — paid once, on delivery, by the split below` },
    { label: 'Collateral', text: 'none — a port run asks no bond' },
    { label: 'Payload', text: `${t.qty} u ${t.familyLabel}` },
    { label: 'Deadline', text: `${hours} h from acceptance — past it the run lapses: crates still aboard go back to the port, the escrow goes home, nobody is paid` },
    { label: 'Loss rule', text: `if your hull is destroyed the run is lost: the escrow goes back to the port and the crates fall into the wreck (2 h, any pilot may strip them); insurance pays your hull only, never the port's cargo` },
    {
      label: 'If the endpoint cannot',
      text: up
        ? `a full yard still takes the ore (stored in its warehouse — no hull until a slipway frees); a yard short of cash short-pays the PORT, never you (your reward is already escrowed)`
        : `the port's warehouse always takes it; the town buys it ${PORT_RELEASE_U} u an hour while its shelf is short`,
    },
  ];
  if (up && t.yardCash !== null && t.yardMark !== null) {
    const q = yardPayQuote(t.yardCash, t.yardMark * BigInt(t.qty));
    out.push({
      label: 'Yard short-pay risk',
      text: q.short > 0n
        ? `the yard owes the port ${sov(q.quote)} (${t.yardMark} ⓒ/u) but holds ${sov(t.yardCash)} — it would pay ${sov(q.pays)}, ${sov(q.short)} short (the port's loss, not yours)`
        : `the yard owes the port ${sov(q.quote)} (${t.yardMark} ⓒ/u) and holds ${sov(t.yardCash)} — paid in full`,
      warn: q.short > 0n,
    });
  }
  const preview = splitPreview(t.rewardSov, t.captainKey, t.crew.map((c) => ({ key: c.key, bps: c.bps })));
  const names = new Map<string, string>([[t.captainKey, `${t.captainName} (captain)`], ...t.crew.map((c) => [c.key, c.name] as [string, string])]);
  out.push({ label: 'Crew split', text: preview.map((p) => `${names.get(p.key) ?? p.key.slice(0, 8)} ${pct(p.bps)} → ${sov(p.sov)}`).join(' · ') + ' — frozen when you accept; the same reward, divided (no second payment)' });
  out.push({ label: 'Relay', text: t.partnerName ? `${t.partnerName} may take the run over ONCE, without you (the rescue path)` : 'none named — you can still offer the run to a pilot from your hold (once)' });
  return out;
}

/** THE DRY CHAIN — one sentence per reason the hourly step gave, quoting its numbers. */
export function dryLines(procure: boolean, reasons: readonly string[], n: Record<string, string | number>): string[] {
  const v = (k: string): string => String(n[k] ?? '?');
  const out: string[] = [];
  for (const r of reasons as DryCode[]) {
    if (r === 'state') out.push(v('state') === 'paused_new' ? 'the port is paused — it offers no new runs' : `the port is ${v('state')}`);
    else if (r === 'pending') out.push('one run is already in flight — the port offers one at a time');
    else if (r === 'yard_ok') out.push(`${v('yardName')} is not short (${v('yardHulls')}/${v('yardTarget')} hulls, ${v('shortHours')} h short of ore) — it orders no ore`);
    else if (r === 'shelf') out.push(`the town keeps ${v('reserve')} u of ore for its smiths and sells only above it — it holds ${v('shelf')} u (a lot needs ${Number(n.lot ?? PORT_LOT_U) + Number(n.reserve ?? 0)})`);
    else if (r === 'cap') out.push(`the export cap is spent — ${v('exported')}/${v('cap')} u this generation`);
    else if (r === 'book') {
      const cost = BigInt(v('cost') === '?' ? '0' : v('cost')), target = BigInt(v('target') === '?' ? '0' : v('target'));
      out.push(`the coin book holds ${v('book')} of the ${v('cost')} coins a lot costs (${v('lot')} × ${v('unit')}/u); the market fund tops it up only to ${v('target')} while the fund holds over ${v('floor')} (it holds ${v('fund')})`
        + (cost > target ? ' — at this price the top-up alone never reaches a lot: only coins from imports the town buys can' : ''));
    } else if (r === 'port_cash') out.push(`the port holds ${v('portCash')} ⓒ — a run would escrow less than the ${v('rewardWant')} ⓒ it should pay (its yard capitalises it only from above ${v('yardReserve')} ⓒ; the yard holds ${v('yardCash')})`);
  }
  if (procure && !out.length) out.push('the port buys a lot at its next hourly step');
  return out;
}

export interface ReceiptView {
  direction: string; stage: string; wait: string; qty: number;
  yardName: string; civ: string;
  captainName: string; delivererName: string; relayFromName: string; custodianRev: number;
  termsVersion: number; splitRev: number;
  reward: bigint; payouts: Array<{ name: string; sov: bigint }>;
  oreQuote: bigint; orePaid: bigint;
  yardOreAfter: number; yardHulls: number; yardTarget: number; shortHours: number; yardOreNow: number | null;
  acceptedMs: number; deliveredMs: number; builtMs: number; reserveMs: number;
  reserveHulls: number; campaignId: bigint; committed: number;
}
export interface ReceiptStep { step: string; text: string; state: 'done' | 'wait' | 'stop' }
const hhmm = (ms: number): string => { const d = new Date(ms); return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`; };

/** THE RECEIPT CHAIN — each link as the data proves it, or the specific reason it stands still. */
export function receiptLines(r: ReceiptView): ReceiptStep[] {
  const out: ReceiptStep[] = [];
  out.push({ step: 'Accepted', state: 'done', text: `${r.captainName} took ${r.qty} u ore at ${hhmm(r.acceptedMs)}${r.termsVersion ? ` (terms v${r.termsVersion}, split rev ${r.splitRev})` : ' (before crew terms — paid whole to the deliverer)'}` });
  out.push({ step: 'Custody', state: 'done', text: r.custodianRev > 0 ? `relayed once — ${r.delivererName} took the same ${r.qty} u over from ${r.relayFromName}` : `carried by ${r.delivererName} the whole way` });
  const paid = r.payouts.length ? r.payouts.map((p) => `${p.name} ${sov(p.sov)}`).join(' · ') : 'nothing (no reward was escrowed)';
  if (r.direction !== 'up') {
    out.push({ step: 'Delivered', state: 'done', text: `landed at the port city at ${hhmm(r.deliveredMs)} by ${r.delivererName}; the escrow paid ${paid} in one posting` });
    out.push({ step: 'The town', state: 'done', text: `the ore waits in the port's surface warehouse — it enters the town's shelf ${PORT_RELEASE_U} u an hour while the shelf is under twice its reserve` });
    return out;
  }
  const short = r.oreQuote - r.orePaid;
  out.push({ step: 'Delivered', state: 'done', text: `landed at ${r.yardName} at ${hhmm(r.deliveredMs)} by ${r.delivererName}; the escrow paid ${paid} in one posting; the yard paid the port ${sov(r.orePaid)} of ${sov(r.oreQuote)}${short > 0n ? ` (${sov(short)} short — the yard's cash ran out)` : ''}` });
  const wasShort = r.shortHours > 0 || r.yardHulls < r.yardTarget;
  out.push({ step: 'Yard order', state: 'done', text: `${r.yardName} ${wasShort ? `was short (${r.yardHulls}/${r.yardTarget} hulls, ${r.shortHours} h short of ore)` : `was not short (${r.yardHulls}/${r.yardTarget} hulls)`} — its ore stock stood at ${r.yardOreAfter} u after this run (${HULL_ORE_U} u make a hull)` });
  const stopped = r.stage === 'stopped';
  // The hull.
  if (r.stage === 'delivered' || (stopped && !r.builtMs)) {
    const why = r.wait === 'yard_cap' ? `yard at cap (${HULLS_MAX}/${HULLS_MAX}) — the ore is stored in its warehouse; no hull until a slipway frees`
      : r.wait === 'ore_short' ? `the yard holds ${r.yardOreNow ?? r.yardOreAfter} of the ${HULL_ORE_U} u a hull needs — waiting for more ore`
      : r.wait === 'followed' ? 'no hull was built while the receipt was followed'
      : 'a hull is laid down at the yard\'s next hourly step';
    out.push({ step: 'Hull', state: stopped ? 'stop' : 'wait', text: why });
  } else out.push({ step: 'Hull', state: 'done', text: `${r.yardName} built a hull at ${hhmm(r.builtMs)} from a stock this run fed (ore is pooled — the receipt never names "this lot's" hull)` });
  if (r.stage === 'delivered') return out;
  // The store.
  if (r.stage === 'built' || (stopped && !r.reserveHulls)) {
    const why = r.wait === 'staffs_patrol' ? `the hull staffs the patrol — the yard keeps ${r.yardTarget}; the civ's store takes only the surplus above that`
      : r.wait === 'store_full' ? 'the civ\'s store is full — the yard keeps its hulls'
      : r.wait === 'followed' ? 'the store took no yard hull while the receipt was followed'
      : 'the civ\'s store takes the yard\'s surplus hulls each hour';
    out.push({ step: 'Reserve', state: stopped ? 'stop' : 'wait', text: why });
    return out;
  }
  out.push({ step: 'Reserve', state: 'done', text: `the ${r.civ} store took ${r.reserveHulls} yard hull${r.reserveHulls === 1 ? '' : 's'} at ${hhmm(r.reserveMs)} (aggregate — hulls are not named)` });
  if (r.stage === 'committed') out.push({ step: 'Campaign', state: 'done', text: `campaign #${r.campaignId} committed ${r.committed} hull-equivalents from the ${r.civ} store (aggregate)` });
  else out.push({ step: 'Campaign', state: stopped ? 'stop' : 'wait', text: stopped ? `no war opened within ${Math.round(RECEIPT_FOLLOW_S / 86_400)} days — the receipt is closed here` : 'no war has opened since — the store holds them' });
  return out;
}

/** A share entered as a percent string ("40", "33.33") → basis points (1 … 10,000), or null. */
export function bpsOf(text: string): number | null {
  const x = Number(String(text).trim().replace('%', ''));
  if (!Number.isFinite(x)) return null;
  const b = Math.round(x * 100);
  return b >= 1 && b <= PORT_SPLIT_BPS ? b : null;
}
